import axios from 'axios';
import { handleAuthError } from '../funciones/authHelper';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Fase 12 · §6.7 — las peticiones de RRHH y Formación en la ficha.
 *
 * ── LA VENTANA VIAJA EN UNA CABECERA, NO EN EL CUERPO ─────────────────────────────────────
 *
 * `x-ventana-2fa` sirve igual para GET que para POST, y así el mismo token se manda del mismo
 * modo en las cinco llamadas. En el cuerpo habría que acordarse en cada una — y en un `GET` no
 * cabe.
 *
 * ── DOS PERMISOS DISTINTOS ────────────────────────────────────────────────────────────────
 *
 *   `personas.datos_rrhh.ver`     → leer y ver las imágenes del DNI
 *   `personas.datos_rrhh.editar`  → guardar, y añadir o quitar hijos
 *
 * No es lo mismo consultar un IBAN que cambiarlo. El servidor los exige por separado; aquí sólo
 * se pintan los botones en consecuencia, que **no es la barrera**: esconder el botón de guardar no
 * impide un PUT.
 */

const cab = (ventana) => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  ...(ventana ? { 'x-ventana-2fa': ventana } : {}),
});

const sufijo = (tipo) => (tipo === 'colaboradora' ? '?tipo=colaboradora' : '');

/* ═══════════════════════════════════════════════════════════════════════════════════════════
 * RRHH · tras la puerta
 * ═══════════════════════════════════════════════════════════════════════════════════════════ */

/** Los datos, descifrados **sólo para esta respuesta**. Sin ventana, el servidor devuelve 401. */
export const fetchDatosRrhh = async (id, ventana, tipo = null) => {
  const r = await axios.get(`${API_URL}/api/personas/${id}/datos-rrhh${sufijo(tipo)}`,
    { headers: cab(ventana) });
  return r.data;
};

/** Guardar. Exige `personas.datos_rrhh.editar` **y** la ventana. */
export const guardarDatosRrhh = async (id, ventana, campos, tipo = null) => {
  const r = await axios.put(`${API_URL}/api/personas/${id}/datos-rrhh${sufijo(tipo)}`,
    { ...campos, ventana }, { headers: cab(ventana) });
  return r.data;
};

export const anadirHijo = async (id, ventana, anioNacimiento, tipo = null) => {
  const r = await axios.post(`${API_URL}/api/personas/${id}/datos-rrhh/hijos${sufijo(tipo)}`,
    { anio_nacimiento: anioNacimiento, ventana }, { headers: cab(ventana) });
  return r.data;
};

export const quitarHijo = async (id, ventana, hijoId, tipo = null) => {
  const r = await axios.delete(`${API_URL}/api/personas/${id}/datos-rrhh/hijos/${hijoId}${sufijo(tipo)}`,
    { headers: cab(ventana), data: { ventana } });
  return r.data;
};

/**
 * Sube una imagen del DNI. Reemplazar una cara borra la anterior del Blob (best-effort).
 *
 * `multipart`, como el adjunto de una titulación. La ventana va en la cabecera, igual que en las
 * demás: en un `FormData` habría que acordarse de añadirla como campo.
 */
export const subirDocumentoIdentidad = async (id, ventana, cara, fichero, tipo = null) => {
  const fd = new FormData();
  fd.append('imagen', fichero);
  const sep = sufijo(tipo) ? '&' : '?';
  const r = await axios.post(
    `${API_URL}/api/personas/${id}/datos-rrhh/documento${sufijo(tipo)}${sep}cara=${cara}`,
    fd, { headers: cab(ventana) });
  return r.data;
};

/**
 * La imagen del DNI, **por proxy**. Devuelve un `blob`.
 *
 * Nunca una URL pública ni un enlace firmado: lo que se comparte deja de caducar, que es el mismo
 * argumento por el que las contraseñas no se exportan. El `objectURL` que se cree con esto vive en
 * la pestaña y se revoca al desmontar.
 */
export const fetchDocumentoIdentidad = async (id, ventana, cara, tipo = null) => {
  const sep = sufijo(tipo) ? '&' : '?';
  const r = await axios.get(
    `${API_URL}/api/personas/${id}/datos-rrhh/documento${sufijo(tipo)}${sep}cara=${cara}`,
    { headers: cab(ventana), responseType: 'blob' });
  return r.data;
};

/* ═══════════════════════════════════════════════════════════════════════════════════════════
 * FORMACIÓN · sin puerta
 * ═══════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Las titulaciones **no llevan ventana**: no son dato confidencial. Un título es algo que la gente
 * pone en su currículum, y exigir aquí el segundo factor sería fricción sin motivo.
 *
 * Que la pestaña esté al lado de RRHH no lo cambia: **la vecindad en una pantalla no es una
 * clasificación.**
 */
export const fetchTitulaciones = async (id, tipo = null) => {
  try {
    const r = await axios.get(`${API_URL}/api/personas/${id}/titulaciones${sufijo(tipo)}`,
      { headers: cab(null) });
    return r.data;
  } catch (error) {
    handleAuthError(error);
    throw error;
  }
};

/** Crear. Va como `multipart` porque puede llevar el adjunto. */
export const crearTitulacion = async (id, campos, fichero, tipo = null) => {
  const fd = new FormData();
  for (const [k, v] of Object.entries(campos)) if (v !== null && v !== undefined) fd.append(k, v);
  if (fichero) fd.append('adjunto', fichero);
  const r = await axios.post(`${API_URL}/api/personas/${id}/titulaciones${sufijo(tipo)}`, fd,
    { headers: cab(null) });
  return r.data;
};

export const editarTitulacion = async (id, titulacionId, campos, tipo = null) => {
  const r = await axios.put(`${API_URL}/api/personas/${id}/titulaciones/${titulacionId}${sufijo(tipo)}`,
    campos, { headers: cab(null) });
  return r.data;
};

/** Borra la fila **y su adjunto del blob**. Ver el controlador: la fila es la verdad. */
export const borrarTitulacion = async (id, titulacionId, tipo = null) => {
  const r = await axios.delete(`${API_URL}/api/personas/${id}/titulaciones/${titulacionId}${sufijo(tipo)}`,
    { headers: cab(null) });
  return r.data;
};

/** El adjunto, por proxy. `blob`, igual que el DNI. */
export const fetchAdjuntoTitulacion = async (id, titulacionId, tipo = null) => {
  const r = await axios.get(
    `${API_URL}/api/personas/${id}/titulaciones/${titulacionId}/adjunto${sufijo(tipo)}`,
    { headers: cab(null), responseType: 'blob' });
  return r.data;
};

/** Abre un blob en una pestaña nueva y revoca la URL. Compartido por el DNI y los adjuntos. */
export const abrirBlob = (blob) => {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  // Se revoca con retraso: revocarla de inmediato deja la pestaña nueva sin nada que cargar.
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};
